/**
 * Activation layer — the part that gets someone moving on a bad day.
 *
 * The measurement loop (check-in → evidence) tells people what movement does
 * for them. It does not help them start. These two mechanics do, and both work
 * for a single user with no social graph:
 *
 * 1. IMPLEMENTATION INTENTIONS — committing to a specific when/where roughly
 *    doubles follow-through versus a vague intention to "exercise more." It is
 *    the strongest non-social behavior-change technique available.
 *
 * 2. PAST-SELF MESSAGES — depression impairs exactly the capacity the app
 *    otherwise depends on (self-motivation). So we capture motivation while the
 *    user HAS it, on a good day, and hand it back on a bad one. A different
 *    version of them supplies the push.
 *
 * Deliberately localStorage-backed: this is a validation build and personal,
 * low-stakes data. Zero setup, works the moment it ships.
 */

const COMMIT_KEY = (userId) => `commitment_${userId || "anon"}`;
const PASTSELF_KEY = (userId) => `pastself_${userId || "anon"}`;

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false; // storage full / private mode — never break the app
  }
}

// ── Commitments ───────────────────────────────────────────────────────────

/** Today's commitment, or null. */
export function getCommitment(userId, date) {
  const c = read(COMMIT_KEY(userId), null);
  if (!c || c.date !== date) return null;
  return c;
}

/**
 * Save a commitment. `time` is "HH:MM" (24h); `what` is the specific plan.
 * Specificity is the active ingredient — "walk around the block" beats "exercise".
 */
export function setCommitment(userId, { date, time, what }) {
  const c = { date, time, what: (what || "").trim(), done: false, createdAt: Date.now() };
  write(COMMIT_KEY(userId), c);
  return c;
}

export function completeCommitment(userId, date) {
  const c = getCommitment(userId, date);
  if (!c) return null;
  const updated = { ...c, done: true, completedAt: Date.now() };
  write(COMMIT_KEY(userId), updated);
  return updated;
}

/**
 * The patient did not do it.
 *
 * There was no way to say this. The only paths off a committed card were "I
 * did it" and silence, which means the record could only ever contain
 * successes and absences — indistinguishable from each other, and useless to
 * the clinician who needs to know whether the plan is too big or the week was.
 *
 * Recorded as a distinct state, never as a failure: `skipped` is a fact about
 * the day, and the card that renders it says so without a single word of
 * repair. A patient who is punished for honest reporting stops reporting
 * honestly, and then the whole instrument is decorative.
 */
export function skipCommitment(userId, date) {
  const c = getCommitment(userId, date);
  if (!c) return null;
  const updated = { ...c, done: false, skipped: true, skippedAt: Date.now() };
  write(COMMIT_KEY(userId), updated);
  return updated;
}

export function clearCommitment(userId) {
  try { localStorage.removeItem(COMMIT_KEY(userId)); } catch { /* ignore */ }
}

/**
 * Commitment status relative to `now`.
 *   none | upcoming | due (within the window) | overdue | done | skipped
 */
export function commitmentStatus(commitment, now = new Date(), windowMinutes = 60) {
  if (!commitment) return "none";
  if (commitment.done) return "done";
  if (commitment.skipped) return "skipped";
  if (!commitment.time) return "upcoming";

  const [h, m] = commitment.time.split(":").map(Number);
  if (Number.isNaN(h)) return "upcoming";

  const target = new Date(now);
  target.setHours(h, m || 0, 0, 0);
  const diffMin = (now - target) / 60000;

  if (diffMin < 0) return "upcoming";
  if (diffMin <= windowMinutes) return "due";
  return "overdue";
}

/** "19:30" → "7:30 PM" */
export function formatTime(time) {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  if (Number.isNaN(h)) return time;
  const period = h >= 12 ? "PM" : "AM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m || 0).padStart(2, "0")} ${period}`;
}

// ── Past-self messages ────────────────────────────────────────────────────

export const GOOD_DAY_MOOD = 4;   // at or above: eligible to record
export const BAD_DAY_MOOD = 2;    // at or below: surface it
const MESSAGE_MAX_AGE_DAYS = 90;

export function getPastSelfMessage(userId) {
  const m = read(PASTSELF_KEY(userId), null);
  if (!m?.text) return null;
  const ageDays = (Date.now() - (m.createdAt || 0)) / 86400000;
  if (ageDays > MESSAGE_MAX_AGE_DAYS) return null; // stale voice, let them re-record
  return m;
}

export function setPastSelfMessage(userId, text) {
  const m = { text: (text || "").trim().slice(0, 500), createdAt: Date.now() };
  if (!m.text) return null;
  write(PASTSELF_KEY(userId), m);
  return m;
}

export function clearPastSelfMessage(userId) {
  try { localStorage.removeItem(PASTSELF_KEY(userId)); } catch { /* ignore */ }
}

/**
 * Ask for a message only on a genuinely good day, and only when there isn't a
 * usable one already. Asking on a bad day would be tone-deaf and useless.
 */
export function shouldAskForMessage(mood, existing) {
  if (mood == null || mood < GOOD_DAY_MOOD) return false;
  return !existing;
}

/** Surface the message only when it's actually needed. */
export function shouldShowMessage(mood, existing) {
  if (!existing?.text) return false;
  return mood != null && mood <= BAD_DAY_MOOD;
}
